import { Search } from "lucide-react";
import React, { useRef, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import images from "../../assets/images";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { DateSelector } from "../../components/DateSelector/DateSelector";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { ColDef } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { Budget, StatusCounts, PremisesData } from "./types/budgeting";
import {
  fetchBudgetData,
  fetchPremisesData,
} from "./services/budgetingService";
import Modal from "../../components/modal/Modal";
import { getS3SignedUrl } from "../../utils/s3Client";
import DocumentViewer from "../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../components/pdf/PdfViewer";
import { XIcon } from "lucide-react";
// import { getStatusColor } from "./utils/budgetingUtils";
// import { StatusBadge } from "./components/budgeting";
import { usePermissions } from "../../hooks/usePermission";
import { BUDGET_MODULE } from "../../constants/modules";
import { useLocation } from "react-router-dom";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

const Budgeting: React.FC = () => {
  const [rowData, setRowData] = useState<Budget[]>([]);
  const [filteredRowData, setFilteredRowData] = useState<Budget[]>([]);
  const [premisesData, setPremisesData] = useState<PremisesData[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedBudgetDocuments, setSelectedBudgetDocuments] = useState<
    { name: string; url: string }[]
  >([]);
  const [currentDocument, setCurrentDocument] = useState<{
    url: string;
    name: string;
    type: string;
  }>({ url: "", name: "", type: "" });
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const { canCreate, canRead, canUpdate } = usePermissions();

  const gridRef = useRef<any>(null);

  const location = useLocation();

  const getModuleName = (): string | undefined => {
    const path = location.pathname;
    if (path.includes("/equipment-management")) {
      return "equipment_management";
    } else if (path.includes("/hazardous-materials")) {
      return "hazardous_materials";
    } else if (
      path.includes("/fire-system") ||
      path.startsWith("/risk-assessment")
    ) {
      return "fire_safety";
    }
    return undefined;
  };
  // Date Range State
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({ start: null, end: null });

  const moduleName = getModuleName();

  useEffect(() => {
    loadPremisesData();
  }, []);

  useEffect(() => {
    if (premisesData.length > 0) {
      loadBudgetData();
    }
  }, [premisesData, moduleName, selectedRegion, selectedPremises]);

  const handleViewDocuments = (documents: string[]) => {
    const formattedDocs = documents.map((url) => ({
      name: url.split("/").pop() || "Document",
      url,
    }));
    setSelectedBudgetDocuments(formattedDocs);
    setCurrentPage(1);
    setIsDocumentModalOpen(true);
  };

  const getMimeType = (extension: string): string => {
    switch (extension.toLowerCase()) {
      case "pdf":
        return "application/pdf";
      case "doc":
      case "docx":
        return "application/msword";
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      case "png":
        return "image/png";
      case "txt":
        return "text/plain";
      default:
        return "application/octet-stream";
    }
  };

  const handleViewDocument = async (documentUrl: string) => {
    try {
      setIsLoadingDocument(true);
      const fileName = documentUrl.split("/").pop() || "";
      const fileType = fileName.split(".").pop()?.toLowerCase() || "";

      const urlToView = await getS3SignedUrl(
        documentUrl,
        getMimeType(fileType),
      );

      setCurrentDocument({
        url: urlToView,
        name: fileName,
        type: fileType,
      });
      setIsDocumentViewerOpen(true);
    } catch (err) {
      console.error("Error loading document:", err);
      setError(
        `Failed to load document: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const handleDownloadDocument = async (documentUrl: string) => {
    try {
      const fileName = documentUrl.split("/").pop() || "";
      const fileType = fileName.split(".").pop()?.toLowerCase() || "";
      const urlToDownload = await getS3SignedUrl(
        documentUrl,
        getMimeType(fileType),
      );
      window.open(urlToDownload, "_blank");
    } catch (err) {
      console.error("Error downloading document:", err);
      setError(
        `Failed to download document: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  };

  const handleCloseDocumentViewer = () => {
    setIsDocumentViewerOpen(false);
    setCurrentDocument({ url: "", name: "", type: "" });
    setError(""); // Clear any errors when closing
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const paginatedDocuments = selectedBudgetDocuments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const totalPages = Math.ceil(selectedBudgetDocuments.length / itemsPerPage);

  const loadPremisesData = async () => {
    try {
      const data = await fetchPremisesData();
      setPremisesData(data);
    } catch (err: any) {
      console.error("Error fetching premises data:", err);
      setError("Failed to load premises data. Please try again later.");
    }
  };

  const loadBudgetData = async () => {
    setIsLoading(true);
    try {
      const data = await fetchBudgetData(
        selectedPremises || undefined,
        moduleName,
        selectedRegion || undefined,
      );

      if (data && data.records) {
        // Map premises ID to premises name for display
        const mappedData = data.records.map((budget: Budget) => {
          const premises = premisesData.find((p) => p.id === budget.premises);
          return {
            ...budget,
            premisesName: premises?.premisesName || budget.premises,
            region: premises?.region || budget.region,
          };
        });
        setRowData(mappedData);
        setFilteredRowData(mappedData);
      } else {
        setRowData([]);
        setFilteredRowData([]);
      }
    } catch (err: any) {
      console.error("Error fetching budget data:", err);
      setError("Failed to load budget data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStatusCountsFromData = (data: Budget[]) => {
    const counts: StatusCounts = {
      budgetingSubmittedCount: 0,
      waitingForApprovalsCount: 0,
      expiredBudgetApprovalCount: 0,
      rejectedItemsCount: 0,
    };

    data.forEach((budget) => {
      switch (budget.status?.toLowerCase()) {
        case "pending":
        case "submitted":
          counts.budgetingSubmittedCount++;
          break;

        case "waiting for approvals":
        case "in review":
          counts.waitingForApprovalsCount++;
          break;

        case "expired":
          counts.expiredBudgetApprovalCount++;
          break;

        case "rejected":
          budget.categories?.forEach((category) => {
            category.items?.forEach((item) => {
              if (item.approvalStatus?.toLowerCase() === "rejected") {
                counts.rejectedItemsCount++;
              }
            });
          });
          break;
      }
    });

    return counts;
  };

  const displayedRowData = useMemo(() => {
    return rowData.filter((row: Budget) => {
      if (dateRange.start && dateRange.end && (row as any).createdAt) {
        const rowDate = new Date((row as any).createdAt);

        const start = new Date(dateRange.start);
        start.setHours(0, 0, 0, 0);

        const end = new Date(dateRange.end);
        end.setHours(23, 59, 59, 999);

        if (rowDate < start || rowDate > end) {
          return false;
        }
      }

      return true;
    });
  }, [rowData, dateRange.start, dateRange.end]);

  const statusCounts = useMemo(() => {
    return calculateStatusCountsFromData(displayedRowData);
  }, [displayedRowData]);

  useEffect(() => {
    let filtered = rowData;

    // Filter by Created On date
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter((item: any) => {
        if (!item.createdAt) return false;

        const rowDate = new Date(item.createdAt);

        const start = new Date(dateRange.start!);
        start.setHours(0, 0, 0, 0);

        const end = new Date(dateRange.end!);
        end.setHours(23, 59, 59, 999);

        return rowDate >= start && rowDate <= end;
      });
    }
    // Search functionality
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((budget) => {
        const premises = premisesData.find((p) => p.id === budget.premises);
        const premisesName = premises?.premisesName || "";

        if (
          (budget.requestId &&
            budget.requestId.toLowerCase().includes(query)) ||
          (premisesName && premisesName.toLowerCase().includes(query)) ||
          (budget.premisesAddress &&
            budget.premisesAddress.toLowerCase().includes(query)) ||
          (budget.requestedBy &&
            budget.requestedBy.toLowerCase().includes(query)) ||
          (budget.approvalManager &&
            budget.approvalManager.toLowerCase().includes(query)) ||
          (budget.status && budget.status.toLowerCase().includes(query)) ||
          (budget.totalCost && budget.totalCost.toString().includes(query))
        ) {
          return true;
        }

        if (budget.categories) {
          for (const category of budget.categories) {
            if (category.category.toLowerCase().includes(query)) {
              return true;
            }

            if (category.vendor) {
              if (
                (category.vendor.name &&
                  category.vendor.name.toLowerCase().includes(query)) ||
                (category.vendor.email &&
                  category.vendor.email.toLowerCase().includes(query)) ||
                (category.vendor.phoneNumber &&
                  category.vendor.phoneNumber.includes(query)) ||
                (category.vendor.websiteAddress &&
                  category.vendor.websiteAddress.toLowerCase().includes(query))
              ) {
                return true;
              }
            }

            if (category.items) {
              for (const item of category.items) {
                if (
                  (item.name && item.name.toLowerCase().includes(query)) ||
                  (item.description &&
                    item.description.toLowerCase().includes(query)) ||
                  (item.justification &&
                    item.justification.toLowerCase().includes(query)) ||
                  (item.approvalStatus &&
                    item.approvalStatus.toLowerCase().includes(query)) ||
                  (item.riskMitigationPlan &&
                    item.riskMitigationPlan.toLowerCase().includes(query)) ||
                  (item.numbersNeeded &&
                    item.numbersNeeded.toString().includes(query)) ||
                  (item.expectedCostPerUnit &&
                    item.expectedCostPerUnit.toString().includes(query)) ||
                  (item.totalCost && item.totalCost.toString().includes(query))
                ) {
                  return true;
                }
              }
            }
          }
        }

        if (budget.emailOptions) {
          for (const emailOption of budget.emailOptions) {
            if (
              emailOption.email &&
              emailOption.email.toLowerCase().includes(query)
            ) {
              return true;
            }
          }
        }

        return false;
      });
    }

    setFilteredRowData(filtered);
  }, [dateRange, searchQuery, rowData, premisesData]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    setSelectedPremises("");
  };

  const handlePremisesChange = (premises: string) => {
    setSelectedPremises(premises);
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "Request ID",
      field: "requestId",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0", textAlign: "center" },
      headerClass: "ag-header-cell-center",
    },
    {
      headerName: "Premises",
      field: "premisesName", // This will display the premises name
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0", textAlign: "center" },
      headerClass: "ag-header-cell-center",
    },
    {
      headerName: "Required By",
      field: "requestedBy",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0", textAlign: "center" },
      headerClass: "ag-header-cell-center",
    },
    {
      headerName: "Approval Manager",
      field: "approvalManager",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0", textAlign: "center" },
      headerClass: "ag-header-cell-center",
    },
    {
      headerName: "Total Cost",
      field: "totalCost",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0", textAlign: "center" },
      headerClass: "ag-header-cell-center",
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return "";
        return `${Number(params.value).toFixed(2)}`;
      },
    },
    {
      headerName: "Documents",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0", textAlign: "center" },
      headerClass: "ag-header-cell-center",
      cellRenderer: (params: any) => {
        const documents =
          params.data.categories?.flatMap(
            (category: any) => category.files || [],
          ) || [];

        return (
          <button
            onClick={() => handleViewDocuments(documents)}
            className="text-blue-600 hover:underline"
          >
            View ({documents.length})
          </button>
        );
      },
    },

    {
      headerName: "Created On",
      field: "createdAt",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0", textAlign: "center" },
      headerClass: "ag-header-cell-center",
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      },
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      headerClass: "ag-header-cell-center",
      cellStyle: (params) => {
        let color = "black";
        switch (params.value?.toLowerCase()) {
          case "approved":
            color = "#4CAF50";
            break;
          case "pending":
          case "submitted":
            color = "#FFC107";
            break;
          case "rejected":
            color = "#F44336";
            break;
          case "in review":
            color = "#FF9800";
            break;
          case "waiting for approvals":
            color = "#2196F3";
            break;
          case "expired":
            color = "#9E9E9E";
            break;
          case "partially approved":
            color = "#8BC34A";
            break;
        }
        return {
          color: color,
          fontWeight: "bold",
          padding: "10px 0",
          textAlign: "center",
        };
      },
    },
    {
      headerName: "Action",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0", textAlign: "center" },
      headerClass: "ag-header-cell-center",
      cellRenderer: (params: any) => {
        const budgetId = params.data.id;

        return (
          <div className="flex items-center justify-center gap-2">
            {canRead(BUDGET_MODULE) && (
              <Link
                to={`/view-budgeting/${budgetId}`}
                state={{
                  returnPath: location.pathname,
                  moduleName: moduleName,
                }}
              >
                <img className="iconTd" src={images.viewIcon} alt="View" />
              </Link>
            )}
            {canUpdate(BUDGET_MODULE) && (
              <Link
                to={`/update-budgeting/${budgetId}`}
                state={{
                  returnPath: location.pathname,
                  moduleName: moduleName,
                }}
              >
                <img className="iconTd" src={images.editIcon} alt="Edit" />
              </Link>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <style>
        {`
          .ag-header-cell-center .ag-header-cell-label {
            justify-content: center;
          }
          .budgeting-container {
            width: 100%;
            max-width: 100%; /* Ensure it doesn't exceed the header width */
            padding: 0 25px; /* Match the header's padding */
            margin: 0 auto; /* Center the content if needed */
          }
          .tableSearchBlock, .cardNum, .p-4 {
            width: 100%; /* Ensure full width within container */
            padding: 0; /* Remove extra padding */
            margin: 0; /* Remove extra margins */
          }
          .ag-theme-alpine {
            width: 100%; /* Ensure the table matches the container width */
            margin: 0; /* Remove extra margins */
          }
        `}
      </style>
      <div className="flex w-full py-4 mt-20">
        <div className="budgeting-container px-2">
          <div className="tableSearchBlock d-flex justify-content-between">
            <div className="leftTableSearchBlock">
              <h2>
                Budgeting For{" "}
                {moduleName === "equipment_management"
                  ? "Equipment Management"
                  : moduleName === "hazardous_materials"
                    ? "Hazardous Materials"
                    : "Fire Safety System"}
              </h2>
            </div>
            <div className="flex gap-4 items-center">
              <Dropdown
                type="region"
                selectedValue={selectedRegion}
                onChange={handleRegionChange}
              />
              <Dropdown
                type="premises"
                selectedValue={selectedPremises}
                onChange={handlePremisesChange}
                selectedRegion={selectedRegion}
              />
              <DateSelector
                onDateChange={(start, end) => setDateRange({ start, end })}
              />
            </div>
          </div>
          <div className="cardNum mt-4">
            <div className="row">
              <div className="col-sm-3">
                <div className="card">
                  <div className="cardIconCont">
                    <span className="font-medium text-md">
                      Budgeting Submitted
                    </span>
                    <h3>{statusCounts.budgetingSubmittedCount}</h3>
                  </div>
                  <div className="cardIcon cardIconColor1">
                    <img className="iconTd" src={images.premisesIcon} alt="" />
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card">
                  <div className="cardIconCont">
                    <span className="font-medium text-md">
                      Waiting For Approvals
                    </span>
                    <h3>{statusCounts.waitingForApprovalsCount}</h3>
                  </div>
                  <div className="cardIcon cardIconColor2">
                    <img className="iconTd" src={images.premisesIcon} alt="" />
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card w-[300px] bg-red-500">
                  <div className="cardIconCont">
                    <span className="font-medium text-sm">
                      Expired Budget Approval
                    </span>
                    <h3>{statusCounts.expiredBudgetApprovalCount}</h3>
                  </div>
                  <div className="cardIcon cardIconColor3">
                    <img className="iconTd" src={images.premisesIcon} alt="" />
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card">
                  <div className="cardIconCont">
                    <span className="font-medium text-md">Rejected Items</span>
                    <h3>{statusCounts.rejectedItemsCount}</h3>
                  </div>
                  <div className="cardIcon cardIconColor4">
                    <img className="iconTd" src={images.premisesIcon} alt="" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 bg-[#ffffff] rounded-md">
            <div className="relative flex justify-between items-center">
              <div className="flex w-10/12 justify-start items-center">
                <Search className="absolute left-3 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search budgets, categories, items..."
                  className="border-2 border-[#a5b7cf] mr-5 w-[] px-10 py-2 rounded"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="dashHeaderRight w-full">
                {canCreate(BUDGET_MODULE) && (
                  <Link
                    to="/create-budgeting"
                    state={{
                      returnPath: location.pathname,
                      moduleName: moduleName,
                    }}
                    className="flex btn-primary roundedBt py-2 iconWithBt text-uppercase"
                  >
                    <img src={images.addPremiessIcon} alt="" />
                    <span>
                      {moduleName === "equipment_management"
                        ? "CREATE BUDGETING FOR EQUIPMENT MANAGEMENT"
                        : moduleName === "hazardous_materials"
                          ? "CREATE BUDGETING FOR HAZARDOUS MATERIALS"
                          : "CREATE BUDGETING FOR FIRE SAFETY SYSTEM"}
                    </span>
                  </Link>
                )}
              </div>
            </div>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
                {error}
              </div>
            )}
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="ag-theme-alpine mt-4">
                <AgGridReact
                  ref={gridRef}
                  rowData={filteredRowData}
                  columnDefs={columnDefs}
                  rowHeight={50}
                  headerHeight={65}
                  paginationPageSize={5}
                  pagination={true}
                  domLayout="autoHeight"
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                  }}
                  getRowStyle={() => ({
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    marginBottom: "8px",
                    borderRadius: "4px",
                    backgroundColor: "white",
                  })}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Document List Modal - Lower z-index */}
      {isDocumentModalOpen && (
        <Modal
          isOpen={isDocumentModalOpen}
          modalClass="bg-white w-full max-w-4xl max-h-[80vh] rounded-xl shadow-lg flex flex-col z-[100]"
          onClose={() => setIsDocumentModalOpen(false)}
          closeButton={false}
        >
          <div className="flex flex-col h-full">
            <div className="flex-none px-6 pt-6">
              <h2 className="text-2xl font-semibold">Documents List</h2>
              <hr className="mt-2 border-2 border-gray-400" />
            </div>

            <div className="max-h-[400px] overflow-y-auto">
              {selectedBudgetDocuments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="py-2 px-4 border-b border-gray-200 text-left">
                          Document Name
                        </th>
                        <th className="py-2 px-4 border-b border-gray-200 text-left">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedDocuments.map((doc, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="py-2 px-4 border-b border-gray-200">
                            {doc.name}
                          </td>
                          <td className="py-2 px-4 border-b border-gray-200">
                            <button
                              onClick={() => handleViewDocument(doc.url)}
                              className="text-blue-600 hover:underline mr-3"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDownloadDocument(doc.url)}
                              className="text-blue-600 hover:underline"
                            >
                              Download
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-4 text-center">
                  No documents available for this budget.
                </p>
              )}
            </div>

            {selectedBudgetDocuments.length > itemsPerPage && (
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded ${
                    currentPage === 1
                      ? "bg-gray-200 cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
                >
                  Previous
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded ${
                    currentPage === totalPages
                      ? "bg-gray-200 cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
            <div className="flex-none py-3 px-6 bg-gray-600 flex justify-end gap-4 rounded-bl-xl rounded-br-xl">
              <button
                type="button"
                className="bg-gray-300 text-gray-800 px-6 py-2 rounded-full"
                onClick={() => setIsDocumentModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Document Viewer Modal - Higher z-index */}
      {isDocumentViewerOpen && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-45">
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col">
            <div className="absolute right-4 top-4 z-10">
              <button
                onClick={handleCloseDocumentViewer}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                aria-label="Close document viewer"
              >
                <XIcon size={20} />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              {isLoadingDocument ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : currentDocument.url ? (
                currentDocument.type === "pdf" ? (
                  <div className="w-full h-full">
                    <PdfViewer fileUrl={currentDocument.url} />
                  </div>
                ) : (
                  <div className="w-full h-full">
                    <DocumentViewer
                      file={currentDocument.url}
                      name={currentDocument.name}
                    />
                  </div>
                )
              ) : (
                <div className="flex justify-center items-center h-full">
                  <p>No document to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Budgeting;
